import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./ComboBox.module.css";
import { getChildrenCommonCode } from "@maxis/client-logic/utils/commonCodeUtil";
import useFetch from "@maxis/client-logic/hooks/useFetch";
import { getValueByKey, normalizeArrayData } from "@maxis/client-logic/utils/dataShapeUtil";
import _ from "lodash";

/**
 * ComboBox
 * @type React.FC<ComboBoxPropTypes>
 */
const ComboBox = React.forwardRef((props, ref) => {
  const { request } = useFetch();
  const refSelect = useRef();

  const [currState, setCurrState] = useState({
    disabled: props.disabled,
    selectedIndex: 0,
    selectedValue: "",
    selectedData: null,
    dataSource: props.dataSource || [],
  });

  const latestValueRef = useRef(currState.selectedValue);
  useEffect(() => {
    latestValueRef.current = currState.selectedValue; // 초기 상태 반영
  }, [currState.selectedValue]);

  Object.keys(props).forEach((p) => {
    if (!Object.keys(ComboBoxPropTypes).includes(p)) {
      console.error(`ComboBox: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    getValue() {
      return currState.selectedValue;
    },
    getCurrValue() {
      return latestValueRef.current; // Ref에서 최신 값 반환
    },
    setValue(v) {
      const event = { target: { value: v } };
      _setValue(event);
    },
    getSelectedData() {
      const { selectedData } = currState;
      return selectedData;
    },
    getDataSource() {
      const { dataSource } = currState;
      return dataSource;
    },
    setValueByLabel(l) {
      const { dataSource } = currState;
      const found = dataSource?.find((e) => getItemValue(e, props.labelId) === l);

      if (found) {
        setCurrState((prev) => ({
          ...prev,
          selectedValue: getItemValue(found, props.valueId),
          selectedData: found,
        }));
      } else {
        setCurrState((prev) => ({
          ...prev,
          selectedValue: "",
          selectedData: null,
        }));
      }
    },
    setDataBindingByAPI(url, method, body, _setVal = "", _callback) {
      _getDataFromAPIAndSet(url, method, body, _setVal, _callback);
    },
    clear() {
      const { dataSource } = currState;

      const defaultSelected = props.defaultValue
        ? dataSource?.findIndex((x, idx) => {
            return (
              getItemValue(x, props.valueId) === props.defaultValue ||
              idx === Number(props.defaultValue)
            );
          })
        : 0;

      const firstRow =
        dataSource.length > 0 ? dataSource[defaultSelected] : null;
      const firstRowValue =
        dataSource.length > 0 ? getItemValue(dataSource[defaultSelected], props.valueId) : "";

      setCurrState((prev) => ({
        ...prev,
        selectedValue: firstRowValue,
        selectedData: firstRow,
        disabled: props.disabled,
      }));
    },
    setDataBinding(source, _setVal = "", _callback) {
      _setDataBinding(source, _setVal, _callback);
    },
    enable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: false,
      }));
    },
    disable() {
      setCurrState((prev) => ({
        ...prev,
        disabled: true,
      }));
    },
    async setSort(v) {
      _setSort(v);
    },

    updateState(stateName, newValue) {
      // currState에 있는 값만 업데이트 가능
      if (Object.keys(currState).includes(stateName)) {
        setCurrState((prev) => ({
          ...prev,
          [stateName]: newValue,
        }));
      } else {
        console.error(
          "ComboBox: State에 지정되지 않은 값을 업데이트 시도하였습니다. 잘못된 시도입니다."
        );
      }
    },
  }));

  // url이 지정된 경우, API를 통해 데이터 가져와 바인딩
  const _getDataFromAPIAndSet = async (
    url,
    method,
    body,
    _setVal = "",
    _callback
  ) => {
    const response = await request(
      url,
      { method: method ?? "GET", ...(body && url ? { body: body } : body) },
      { showConfirm: false, showLoad: false }
    );
    if (response) {
      _setDataBinding(
        url ? response : response.data[props.respName],
        _setVal,
        _callback
      );
    }
  };

  useEffect(() => {
    if (props.dataSource) {
      _setDataBinding(props.dataSource);
      return;
    }

    // Combobox 렌더링 후, 데이터 바인딩
    // 공통코드 데이터인 경우, LocalStorage에서 가져와 바인딩
    const getCommonCodesAndSet = async (parentCodeId) => {
      const codes = await getChildrenCommonCode(parentCodeId);
      if (codes?.length > 0) {
        _setDataBinding(codes);
      }
      return;
    };

    if (props.parentCodeId) {
      getCommonCodesAndSet(props.parentCodeId);
      return;
    }

    if (props.url || (!props.url && props.apiBody)) {
      _getDataFromAPIAndSet(props.url, props.apiMethod, props.apiBody);
    }
  }, []);

  const _setDataBinding = (source, _setVal = "", _callback) => {
    let data = normalizeDataSource(source);
    if (props.sort) _sorting(data, props.sort);
    if (props.addAll) {
      data = _addAll(data);
    }
    if (props.addBlank) {
      data = _addBlank(data);
    }

    if (props.addText) {
      data = _addText(data, props.addText);
    }

    const defaultSelected = props.defaultValue
      ? data?.findIndex((x, idx) => {
          return (
            getItemValue(x, props.valueId) === props.defaultValue ||
            idx === Number(props.defaultValue)
          );
        })
      : 0;

    const setVal = _setVal
      ? data?.findIndex((x, idx) => {
          return getItemValue(x, props.valueId) === _setVal || idx === Number(_setVal);
        })
      : -1;

    // 첫번째 데이터 기본 선택
    const selectedData =
      data?.length > 0 ? data[setVal !== -1 ? setVal : defaultSelected] : null;
    const selectedValue = selectedData ? getItemValue(selectedData, props.valueId) : "";

    setCurrState((prev) => ({
      ...prev,
      selectedData: selectedData,
      selectedValue: selectedValue,
      dataSource: data,
    }));

    if (_callback > "") _callback(this);
  };

  const normalizeDataSource = (source) => {
    return normalizeArrayData(source);
  };

  const getItemValue = (item, key) => {
    return getValueByKey(item, key);
  };

  const _addBlank = (data = []) => {
    const blank = {
      [props.valueId]: "",
      [props.labelId]: "",
      ord: 0,
    };

    return [blank, ...data];
  };

  const _addAll = (data = []) => {
    const all = {
      [props.valueId]: "",
      [props.labelId]: [props.addAllPlaceholder],
      ord: 0,
    };

    return [all, ...data];
  };

  const _addText = (data = [], text) => {
    const blank = {
      [props.valueId]: "",
      [props.labelId]: text,
      ord: 0,
    };

    return [blank, ...data];
  };

  const _setValue = (event) => {
    const { value } = event.target;
    const { dataSource } = currState || [];
    const found = dataSource.find((data) => getItemValue(data, props.valueId) == value);

    if (found) {
      setCurrState((prev) => ({
        ...prev,
        selectedValue: value,
        selectedData: found,
      }));
    } else {
      setCurrState((prev) => ({
        ...prev,
        selectedValue: "",
        selectedData: null,
      }));
    }

    latestValueRef.current = found ? value : "";

    if (props.onChange) {
      props.onChange(event, found);
    }
  };

  const _onChange = (e) => {
    _setValue(e);
  };

  const _onKeyDown = (e) => {
    if (e.key === "Enter" && props.onEnterPress) props.onEnterPress(e);
  };

  /**
   * sorting
   * @param {*} codes
   * @param {*} v
   * @returns
   */
  const _sorting = async (codes = [], v) => {
    if (!codes.length || !v?.field) return codes;

    const { field: key, order = "asc" } = v;
    const isValueNumeric = codes[0][key] !== "" && !isNaN(codes[0][key]);

    const compare = (a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (isValueNumeric) {
        return aValue - bValue;
      } else {
        return aValue.localeCompare(bValue);
      }
    };

    codes.sort((a, b) => (order === "desc" ? compare(b, a) : compare(a, b)));

    return codes;
  };

  /**
   * sorting
   * @param {*} v
   */
  const _setSort = async (v) => {
    const { dataSource } = currState;
    const _data = _.cloneDeep(dataSource);

    _sorting(_data, v).then((codes) => {
      setCurrState((prev) => ({
        ...prev,
        dataSource: codes,
      }));
    });
  };

  const options =
    currState.dataSource.length > 0 &&
    currState.dataSource.map((item, i) => {
      return (
        <option key={i} value={getItemValue(item, props.valueId)}>
          {getItemValue(item, props.labelId)}
        </option>
      );
    });

  return (
    <select
      className={`${styles.e3Select} ${props.controlWidth ?? ""} ${
        props.className ?? ""
      }`}
      ref={refSelect}
      onChange={_onChange}
      onKeyDown={_onKeyDown}
      value={currState.selectedValue}
      disabled={currState.disabled}
    >
      {options}
    </select>
  );
});

const ComboBoxPropTypes = {
  addBlank: PropTypes.bool,
  addAll: PropTypes.bool,
  addAllPlaceholder: PropTypes.string,
  addText: PropTypes.string,
  valueId: PropTypes.string,
  labelId: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onEnterPress: PropTypes.func,
  parentCodeId: PropTypes.string,
  url: PropTypes.string,
  apiMethod: PropTypes.string,
  apiBody: PropTypes.object,
  dataSource: PropTypes.array,
  controlWidth: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  hidden: PropTypes.bool,
  respName: PropTypes.string,
  serviceId: PropTypes.string,
  methodName: PropTypes.string,
  serviceParam: PropTypes.object,
  showLoad: PropTypes.bool,
  sort: PropTypes.object,
};

ComboBox.propTypes = ComboBoxPropTypes;
ComboBox.defaultProps = {
  addBlank: false,
  addAll: false,
  addAllPlaceholder: "전체",
  addText: null,
  valueId: "codeId",
  labelId: "codeName",
  disabled: false,
  onChange: null,
  onEnterPress: null,
  parentCodeId: null,
  url: null,
  apiMethod: null,
  apiBody: null,
  dataSource: null,
  controlWidth: null,
  defaultValue: "",
  className: "",
  hidden: false,
  respName: "result",
  sort: null, // ex) { field: 'ord', order: 'asc' }
};
ComboBox.displayName = "ComboBox";
export default ComboBox;
